import { Award, GraduationCap, Briefcase, ExternalLink, CheckCircle2 } from "lucide-react";
import { Badge } from "./ui/badge";

const About = ({ config, cpiicm, content }) => {
  const sectionLabel = content?.section_label || "Perfil Profesional";

  if (!config) {
    return (
      <section id="sobre-mi" className="py-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-800 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-slate-800 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-slate-800 rounded w-1/2"></div>
          </div>
        </div>
      </section>
    );
  }

  const { personal, colegiacion, listas_judiciales, formacion, certificaciones, experiencia, herramientas } = config;

  return (
    <section id="sobre-mi" className="py-24 relative" data-testid="about-section">
      <div className="absolute inset-0 gradient-overlay opacity-50" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header with shield logo */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <span className="text-sky-400 font-mono text-sm tracking-wider uppercase mb-4 block">
              {sectionLabel}
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
              {personal?.name || "Perito Informatico Colegiado"}
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              {personal?.bio}
            </p>
          </div>
          {/* Shield logo */}
          <div className="hidden lg:flex items-center justify-center">
            <img
              src="/logo-forense.png"
              alt="Escudo Perito Informatico"
              className="w-48 h-auto opacity-70"
              data-testid="about-shield-logo"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left column - Main info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Colegiacion card */}
            <div className="card-service animated-border" data-testid="colegiacion-card">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-16 h-16 rounded-sm bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                  <Award className="w-8 h-8 text-sky-400" />
                </div>
                <div className="flex-grow">
                  <h3 className="font-outfit text-xl font-semibold text-white mb-2">
                    Colegiado CPIICM
                  </h3>
                  <p className="text-slate-400 mb-3">
                    {colegiacion?.colegio}
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    <Badge variant="outline" className="border-sky-400/50 text-sky-400">
                      Numero: {colegiacion?.numero_colegiado || cpiicm?.member_number || "XXXXX"}
                    </Badge>
                    {colegiacion?.url_verificacion && (
                      <a 
                        href={colegiacion.url_verificacion}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-sky-400 transition-colors"
                      >
                        Verificar
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Listas judiciales */}
            {listas_judiciales?.activo && (
              <div className="card-service" data-testid="listas-judiciales-card">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-slate-900 border border-slate-700 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-sky-400" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-lg font-semibold text-white mb-2">
                      Listas de Designacion Judicial
                    </h3>
                    <p className="text-slate-400 text-sm mb-3">
                      {listas_judiciales.descripcion}
                    </p>

                  </div>
                </div>
              </div>
            )}

            {/* Formacion */}
            <div className="card-service" data-testid="formacion-card">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-slate-900 border border-slate-700 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-sky-400" />
                </div>
                <div className="flex-grow">
                  <h3 className="font-outfit text-lg font-semibold text-white mb-4">
                    Formacion Academica
                  </h3>
                  <div className="space-y-3">
                    {formacion?.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start border-b border-slate-700/50 pb-3 last:border-0 last:pb-0">
                        <div>
                          <p className="font-medium text-white">{item.titulo}</p>
                          <p className="text-sm text-slate-500">{item.institucion}</p>
                        </div>
                        <span className="font-mono text-sm text-sky-400">{item.año}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Herramientas */}
            <div className="card-service" data-testid="herramientas-card">
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">
                Herramientas Forenses
              </h3>
              <div className="flex flex-wrap gap-2">
                {herramientas?.map((tool, idx) => (
                  <Badge key={idx} variant="outline" className="border-slate-700 text-slate-400 hover:border-sky-400/50 hover:text-sky-400 transition-colors">
                    {tool}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Stats and certifications */}
          <div className="space-y-8">
            {/* Experience stats */}
            <div className="card-service" data-testid="experience-card">
              <h3 className="font-outfit text-lg font-semibold text-white mb-6">
                Experiencia
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-slate-900/50 rounded-sm border border-slate-700/50">
                  <p className="font-outfit text-3xl font-bold text-sky-400">{experiencia?.años_experiencia}+</p>
                  <p className="text-xs text-slate-500 mt-1">Años experiencia</p>
                </div>
                <div className="text-center p-4 bg-slate-900/50 rounded-sm border border-slate-700/50">
                  <p className="font-outfit text-3xl font-bold text-sky-400">{experiencia?.casos_peritados}%</p>
                  <p className="text-xs text-slate-500 mt-1">Casos de exito</p>
                </div>
                <div className="text-center p-4 bg-slate-900/50 rounded-sm border border-slate-700/50">
                  <p className="font-outfit text-3xl font-bold text-sky-400">{experiencia?.informes_judiciales}%</p>
                  <p className="text-xs text-slate-500 mt-1">Clientes satisfechos</p>
                </div>
                <div className="text-center p-4 bg-slate-900/50 rounded-sm border border-slate-700/50">
                  <p className="font-outfit text-3xl font-bold text-sky-400">{experiencia?.ratificaciones_juzgado}</p>
                  <p className="text-xs text-slate-500 mt-1">--</p>
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="card-service" data-testid="certifications-card">
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">
                Certificaciones
              </h3>
              <div className="space-y-3">
                {certificaciones?.map((cert, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cert.vigente ? 'text-sky-400' : 'text-slate-600'}`} />
                    <div>
                      <p className="text-sm font-medium text-white">{cert.nombre}</p>
                      <p className="text-xs text-slate-500">{cert.emisor}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
